// src/pages/ExpensesPage.tsx
import React, { useState, useEffect } from 'react';
import { Button, Table, Tag, Popconfirm, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ExpenseModal from '../components/ExpenseModal';
import { apiGetExpenses, apiDeleteExpense } from '../services/expenseAPI';
import { formatCurrency } from '../utils/numbers';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  paid:       { label: 'Оплачено',      color: 'green'  },
  planned:    { label: 'Запланировано', color: 'blue'   },
  processing: { label: 'В процессе',    color: 'orange' },
  canceled:   { label: 'Отменено',      color: 'red'    },
};

const buildColumns = (
  onEdit: (r: any) => void,
  onDelete: (id: string) => void,
) => [
  {
    title: 'Дата',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 140,
    render: (v: string) =>
      new Date(v).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }),
    sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    defaultSortOrder: 'descend' as const,
  },
  { title: 'Поставщик', dataIndex: 'supplier', key: 'supplier', width: 160 },
  { title: 'Категория', dataIndex: 'category', key: 'category', width: 160 },
  {
    title: 'Описание',
    dataIndex: 'description',
    key: 'description',
    ellipsis: true,
    render: (_: any, r: any) => {
      const lines = r.lines ?? [];
      if (lines.length) return lines.map((l: any) => l.name).filter(Boolean).join(', ') || r.description;
      return r.description || '—';
    },
  },
  {
    title: 'Расход',
    dataIndex: 'description',
    key: 'type',
    width: 160,
    render: (_: any, r: any) =>
      (r.lines?.length ? 'Позиционный расход' : 'Единичный расход'),
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    width: 140,
    render: (v: string) => {
      const m = STATUS_LABELS[v] ?? { label: v, color: 'default' };
      return <Tag color={m.color}>{m.label}</Tag>;
    },
  },
  {
    title: 'Сумма',
    dataIndex: 'total',
    key: 'total',
    width: 140,
    render: (v: number) => <b>{formatCurrency(v ?? 0, 'AMD')}</b>,
    sorter: (a: any, b: any) => (a.total ?? 0) - (b.total ?? 0),
  },
  {
    title: 'Действия',
    key: 'actions',
    width: 100,
    align: 'center' as const,
    render: (_: any, r: any) => (
      <Space size={12}>
        <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)} />
        <Popconfirm
          title="Удалить расход?"
          okText="Да" cancelText="Нет"
          onConfirm={() => onDelete(r.id)}
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ),
  },
];

const ExpensesPage: React.FC = () => {
  const [records, setRecords]     = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const all = await apiGetExpenses();
      all.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setRecords(all);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (r: any) => {
    setEditRecord(r);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    await apiDeleteExpense(id);
    load();
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditRecord(null);
  };

  const totalSum = records.reduce((s, r) => s + (r.total ?? 0), 0);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Расходы</h2>
          <Tag color="blue" style={{ fontSize: 13 }}>
            Итого: {totalSum.toLocaleString('ru-RU')} ֏
          </Tag>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); setModalOpen(true); }}>
          Добавить расход
        </Button>
      </div>

      <Table
        style={{ marginTop: 16 }}
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={records}
        columns={buildColumns(handleEdit, handleDelete)}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20','50','100'] }}
        scroll={{ x: 'max-content' }}
      />

      <ExpenseModal
        open={modalOpen}
        onClose={handleModalClose}
        onCreated={() => { handleModalClose(); load(); }}
        initialValues={editRecord}
      />
    </div>
  );
};

export default ExpensesPage;
